import { useState } from "react";
import { Loader2 } from "lucide-react";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from "../ui/drawer";

const NEXT_ACTIONS = [
  { value: "none", label: "None" },
  { value: "call", label: "Call" },
  { value: "email", label: "Email" },
  { value: "invoice", label: "Send Invoice" },
  { value: "awaiting_client", label: "Awaiting Client" },
  { value: "follow_up", label: "Follow Up" }
];

const CONFIDENCE_LEVELS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" }
];

function EditForm({ form, setForm, users, saving, onSave, onClose }) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Description</Label>
        <Input
          value={form.description}
          onChange={e => setForm({ ...form, description: e.target.value })}
          placeholder="e.g., Deposit (50%)"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Expected</Label>
          <Input
            type="number"
            value={form.expected_amount}
            onChange={e => setForm({ ...form, expected_amount: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label>Collected</Label>
          <Input
            type="number"
            value={form.collected_amount}
            onChange={e => setForm({ ...form, collected_amount: e.target.value })}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Due Date</Label>
        <Input
          type="date"
          value={form.due_date}
          onChange={e => setForm({ ...form, due_date: e.target.value })}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Owner</Label>
          <Select value={form.owner_id || "none"} onValueChange={v => setForm({ ...form, owner_id: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Unassigned</SelectItem>
              {users?.map(u => (
                <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Next Action</Label>
          <Select value={form.next_action} onValueChange={v => setForm({ ...form, next_action: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {NEXT_ACTIONS.map(a => (
                <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Confidence</Label>
        <Select value={form.confidence} onValueChange={v => setForm({ ...form, confidence: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {CONFIDENCE_LEVELS.map(c => (
              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea
          value={form.notes}
          onChange={e => setForm({ ...form, notes: e.target.value })}
          placeholder="Internal notes..."
          rows={2}
        />
      </div>

      <div className="flex gap-2 pt-2">
        <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
        <Button onClick={onSave} disabled={saving} className="flex-1">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : "Save"}
        </Button>
      </div>
    </div>
  );
}

export default function EditReceivableDialog({ receivable, open, onClose, onSave, users }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    description: receivable?.description || "",
    expected_amount: receivable?.expected_amount || 0,
    collected_amount: receivable?.collected_amount || 0,
    due_date: receivable?.due_date?.split("T")[0] || "",
    owner_id: receivable?.owner_id || "none",
    next_action: receivable?.next_action || "none",
    confidence: receivable?.confidence || "medium",
    confidence_override: receivable?.confidence_override || false,
    notes: receivable?.notes || ""
  });

  async function handleSave() {
    setSaving(true);
    try {
      const payload = {
        ...form,
        expected_amount: parseFloat(form.expected_amount),
        collected_amount: parseFloat(form.collected_amount),
        owner_id: form.owner_id === "none" ? null : form.owner_id,
        confidence_override: form.confidence !== receivable?.confidence
      };
      
      await apiClient.patch(`/receivables/${receivable.id}`, payload);
      toast.success("Receivable updated");
      onSave?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  }

  // Check if mobile (client-side only)
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // Mobile: Use Drawer
  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
        <DrawerContent className="px-4 pb-6">
          <DrawerHeader className="px-0">
            <DrawerTitle style={{ fontFamily: 'var(--font-sans)' }}>Edit Receivable</DrawerTitle>
            <DrawerDescription>Update receivable details</DrawerDescription>
          </DrawerHeader>
          <EditForm 
            form={form} 
            setForm={setForm} 
            users={users} 
            saving={saving} 
            onSave={handleSave} 
            onClose={onClose} 
          />
        </DrawerContent>
      </Drawer>
    );
  }

  // Desktop: Use Dialog
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'var(--font-sans)' }}>Edit Receivable</DialogTitle>
          <DialogDescription>Update receivable details and collection status</DialogDescription>
        </DialogHeader>
        <EditForm 
          form={form} 
          setForm={setForm} 
          users={users} 
          saving={saving} 
          onSave={handleSave} 
          onClose={onClose} 
        />
      </DialogContent>
    </Dialog>
  );
}
